import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHero } from "@/components/Shared";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Results() {
  const [filters, setFilters] = useState({ venues: [], seasons: [], events: [] });
  const [season, setSeason] = useState("all");
  const [venue, setVenue] = useState("all");
  const [event, setEvent] = useState("all");
  const [rows, setRows] = useState([]);

  useEffect(() => {
    api.get("/leaderboards/filters").then((r) => {
      setFilters(r.data);
      if (r.data.default_season) setSeason(r.data.default_season);
    }).catch(() => {});
  }, []);

  useEffect(() => { setEvent("all"); }, [venue, season]);

  const load = useCallback(() => {
    api.get(`/results?season=${encodeURIComponent(season)}&venue=${encodeURIComponent(venue)}&event=${encodeURIComponent(event)}`).then((r) => setRows(r.data)).catch(() => {});
  }, [venue, season, event]);
  useEffect(() => { load(); }, [load]);

  const seasonLabel = (s) => { const r = [s.start_date, s.end_date].filter(Boolean).join(" – "); return r ? `${s.name} (${r})` : s.name; };
  const eventOptions = [...new Map((filters.events || []).filter((e) => (venue === "all" || e.venue === venue) && (season === "all" || e.season === season)).map((e) => [e.title, e])).values()];

  return (
    <div data-testid="results-page">
      <PageHero
        title="Results"
        subtitle="Every placement from every Legends event. Filter by venue and season to see who's been running hot."
        image="https://images.pexels.com/photos/35448122/pexels-photo-35448122.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=1600"
        testId="results-hero"
      />
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-14 md:py-20">
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <Select value={season} onValueChange={setSeason}>
            <SelectTrigger className="w-full sm:w-72 rounded-none bg-navy-paper border-slate-700" data-testid="results-season-filter"><SelectValue placeholder="Season" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Seasons</SelectItem>
              {filters.seasons.map((s) => <SelectItem key={s.name} value={s.name}>{seasonLabel(s)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={venue} onValueChange={setVenue}>
            <SelectTrigger className="w-full sm:w-56 rounded-none bg-navy-paper border-slate-700" data-testid="results-venue-filter"><SelectValue placeholder="Venue" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Venues</SelectItem>
              {filters.venues.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={event} onValueChange={setEvent}>
            <SelectTrigger className="w-full sm:w-64 rounded-none bg-navy-paper border-slate-700" data-testid="results-event-filter"><SelectValue placeholder="Event" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Events</SelectItem>
              {eventOptions.map((e) => <SelectItem key={e.title} value={e.title}>{e.title}{e.date ? ` · ${e.date}` : ""}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="border border-white/10 overflow-x-auto" data-testid="results-table">
          <table className="w-full text-left min-w-[720px]">
            <thead className="bg-secondary">
              <tr className="text-gold text-xs uppercase tracking-wider">
                <th className="px-5 py-4 w-16">Place</th>
                <th className="px-5 py-4">Player</th>
                <th className="px-5 py-4">Event</th>
                <th className="px-5 py-4">Venue</th>
                <th className="px-5 py-4">Date</th>
                <th className="px-5 py-4 text-right">Points</th>
                <th className="px-5 py-4 text-right">Payout</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-white/5 hover:bg-white/5 transition-colors" data-testid={`result-row-${r.id}`}>
                  <td className="px-5 py-3 font-display font-bold text-gold">{r.place}</td>
                  <td className="px-5 py-3 text-white"><Link to={`/players/${encodeURIComponent(r.player_name)}`} className="hover:text-gold transition-colors">{r.player_name}</Link></td>
                  <td className="px-5 py-3 text-slate-400 text-sm">{r.event_title}</td>
                  <td className="px-5 py-3 text-slate-400 text-sm">{r.venue}</td>
                  <td className="px-5 py-3 text-slate-500 text-sm">{r.date}</td>
                  <td className="px-5 py-3 text-right text-slate-200">{r.points}</td>
                  <td className="px-5 py-3 text-right text-slate-300">{r.payout ? `$${r.payout.toLocaleString()}` : "—"}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={7} className="px-5 py-16 text-center text-slate-500">No results found.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
